import { useState } from 'react';
import { Btn, FormRow, Field, FileUpload, Modal, FormActions } from '../../components/ui';
import QuotationService from '../../api/quotation';
import { uploadFiles } from '../../api/files';
import { toast } from 'react-hot-toast';
import { getFieldError } from '../../utils/apiUtils';

const formatGstValue = (val) => {
  if (val === null || val === undefined) return "";
  const str = String(val).trim();
  if (!str) return "";
  const clean = str.replace(/%/g, "").trim();
  if (!clean) return "";
  return `${clean}%`;
};

const createEmptyItem = () => ({
  vendor: "",
  amount: "",
  remarks: "",
  warranty: "",
  timeline: "",
  gst: "",
  rating: ""
});

const DEFAULT_FORM = {
  title: "",
  description: "",
  requested_by: "",
  due_date: "",
  total_vendors_invited: 1,
  items: [createEmptyItem()],
  file_ids: [],
  files: []
};

export default function QuotationModal({
  isOpen,
  onClose,
  editQuotation = null,
  onSaved,
  user,
  t
}) {
  const [form, setForm] = useState(() => {
    if (editQuotation) {
      return {
        title: editQuotation.title || "",
        description: editQuotation.description || "",
        requested_by: editQuotation.requested_by || "",
        due_date: editQuotation.due_date || "",
        total_vendors_invited: editQuotation.total_vendors_invited || 1,
        items: editQuotation.bids && editQuotation.bids.length > 0
          ? editQuotation.bids.map(b => ({
              vendor: b.vendor_name || "",
              amount: b.amount || "",
              remarks: b.remarks || "",
              warranty: b.warranty || "",
              timeline: b.timeline || "",
              gst: formatGstValue(b.gst),
              rating: b.rating || ""
            }))
          : [createEmptyItem()],
        file_ids: editQuotation.file_ids || [],
        files: editQuotation.attachments || []
      };
    }
    return DEFAULT_FORM;
  });

  const [formErrors, setFormErrors] = useState({});
  const [itemErrors, setItemErrors] = useState([]);
  const [saving, setSaving] = useState(false);

  if (!isOpen) return null;

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => {
        const copy = { ...prev };
        delete copy[field];
        return copy;
      });
    }
  };

  const updateItem = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      items: prev.items.map((it, idx) => idx === index ? { ...it, [field]: value } : it)
    }));
    if (itemErrors[index]?.[field]) {
      setItemErrors(prev => {
        if (!prev[index]?.[field]) return prev;
        const copy = [...prev];
        copy[index] = { ...copy[index] };
        delete copy[index][field];
        return copy;
      });
    }
  };

  const handleTotalVendorsChange = (e) => {
    const raw = e.target.value;
    if (raw === "") {
      setForm(f => ({ ...f, total_vendors_invited: "" }));
      return;
    }
    let val = raw;
    // Replace default "1" when user types a new digit (e.g., "5" resulting in "15" or "51")
    if (String(form.total_vendors_invited) === "1" && raw.length === 2 && raw !== "10" && raw !== "11") {
      val = raw.startsWith("1") ? raw.slice(1) : raw.slice(0, 1);
    }
    const count = parseInt(val, 10);
    setForm(f => {
      let newItems = f.items;
      if (!isNaN(count) && count >= 1 && count <= 10 && count !== f.items.length) {
        newItems = [...f.items];
        while (newItems.length < count) {
          newItems.push(createEmptyItem());
        }
        if (newItems.length > count) {
          newItems = newItems.slice(0, count);
        }
      }
      return { ...f, total_vendors_invited: val, items: newItems };
    });
    if (formErrors.total_vendors_invited) {
      setFormErrors(prev => {
        const copy = { ...prev };
        delete copy.total_vendors_invited;
        return copy;
      });
    }
  };

  const removeItem = (index) => {
    setForm(f => {
      const updated = f.items.filter((_, idx) => idx !== index);
      const newCount = Math.max(1, updated.length);
      return {
        ...f,
        total_vendors_invited: newCount,
        items: updated.length > 0 ? updated : [createEmptyItem()]
      };
    });
    setItemErrors(prev => prev.filter((_, idx) => idx !== index));
  };

  const addItem = () => {
    setForm(f => {
      if (f.items.length >= 10) return f;
      const nextCount = f.items.length + 1;
      return {
        ...f,
        total_vendors_invited: nextCount,
        items: [...f.items, createEmptyItem()]
      };
    });
  };

  const handleSubmit = async () => {
    const formattedItems = form.items.map(it => ({
      ...it,
      gst: formatGstValue(it.gst)
    }));

    const errors = {};
    if (!form.title) errors.title = "Title is required";
    if (!form.due_date) errors.due_date = "Due Date is required";
    if (!form.description) errors.description = "Description is required";
    if (!form.total_vendors_invited) errors.total_vendors_invited = "Total Vendors Invited is required";

    const newErrors = [];
    let hasItemErrors = false;
    formattedItems.forEach((it) => {
      const err = {};
      const trimmedVendor = (it.vendor || "").trim();
      if (!trimmedVendor) {
        err.vendor = "Required";
      } else if (!/\p{L}/u.test(trimmedVendor)) {
        err.vendor = t("val_vendor_name_letters", "Vendor name cannot be numeric only");
      }
      if (!it.amount) err.amount = "Required";
      if (!it.gst) err.gst = "Required";
      if (!it.timeline) err.timeline = "Required";
      if (!it.warranty) err.warranty = "Required";
      if (!it.rating && it.rating !== 0) {
        err.rating = "Required";
      } else {
        const r = parseFloat(it.rating);
        if (isNaN(r) || r < 1 || r > 5) {
          err.rating = "Rating must be between 1 and 5";
        }
      }
      if (!it.remarks) err.remarks = "Required";
      newErrors.push(err);
      if (Object.keys(err).length > 0) hasItemErrors = true;
    });

    if (Object.keys(errors).length > 0 || hasItemErrors) {
      setFormErrors(errors);
      setItemErrors(newErrors);
      const hasNumericVendorError = newErrors.some(it => it.vendor && it.vendor !== "Required");
      if (hasNumericVendorError) {
        toast.error(t("val_vendor_name_letters", "Vendor name cannot be numeric only"));
      } else {
        toast.error("Please fill all mandatory fields.");
      }
      return;
    }

    setSaving(true);
    try {
      let qFileIds = [];
      const existingFiles = (form.files || []).filter(f => !(f instanceof File) && f.id);
      qFileIds = existingFiles.map(f => f.id);

      const newFiles = (form.files || []).filter(f => f instanceof File);
      if (newFiles.length > 0) {
        const uploadRes = await uploadFiles(newFiles, 'quotations');
        const uploaded = uploadRes.data?.data?.uploaded || uploadRes.data?.uploaded || uploadRes.uploaded || [];
        qFileIds = [...qFileIds, ...uploaded.map(f => f.id)];
      }

      const payload = {
        title: form.title,
        description: form.description,
        requested_by: user?.name || "System User",
        due_date: form.due_date,
        total_vendors_invited: parseInt(form.total_vendors_invited, 10) || 1,
        items: formattedItems,
        file_ids: qFileIds
      };

      if (editQuotation?.id) {
        const updatedData = await QuotationService.updateQuotation(editQuotation.id, payload);
        const updatedItem = updatedData.data || updatedData;
        toast.success(t("toast_update_quotation_success"));
        onSaved(updatedItem, true);
      } else {
        const createdData = await QuotationService.createQuotation(payload);
        const createdItem = createdData.data || createdData;
        toast.success(t("toast_create_quotation_success"));
        onSaved(createdItem, false);
      }
      onClose();
    } catch (err) {
      toast.error(getFieldError(err) || t("toast_failed_to_save"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={editQuotation ? t("title_edit_quotation") : t("title_new_quotation")}
      onClose={onClose}
      wide
    >
      <FormRow>
        <Field
          label={t("label_title")}
          value={form.title}
          onChange={e => updateField("title", e.target.value)}
          required
          error={formErrors.title}
        />
      </FormRow>

      <FormRow cols={2}>
        <Field
          label="Due Date"
          type="date"
          value={form.due_date}
          onChange={e => updateField("due_date", e.target.value)}
          required
          error={formErrors.due_date}
        />
        <Field
          label="Total Vendors Invited"
          type="number"
          value={form.total_vendors_invited}
          onChange={handleTotalVendorsChange}
          min="1"
          max="10"
          required
          error={formErrors.total_vendors_invited}
        />
      </FormRow>

      <FormRow>
        <Field
          label={t("label_description_scope")}
          type="textarea"
          value={form.description}
          onChange={e => updateField("description", e.target.value)}
          rows={2}
          required
          error={formErrors.description}
        />
      </FormRow>

      <div className="mb-3">
        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
          {t("label_vendor_bids")}
        </label>
        {form.items.map((item, i) => (
          <div key={i} className="mb-4 p-3 border border-border rounded-lg bg-slate-50 dark:bg-slate-800/50 relative">
            <button
              type="button"
              onClick={() => removeItem(i)}
              className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center border border-red-200 text-red-400 rounded-full hover:bg-red-50 dark:hover:bg-red-950/20 cursor-pointer bg-white dark:bg-slate-900 text-xs"
            >
              ×
            </button>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pr-8">
              <div className="col-span-1 sm:col-span-2 lg:col-span-1">
                <input
                  placeholder="Vendor Name *"
                  value={item.vendor}
                  onChange={e => {
                    const val = e.target.value;
                    updateItem(i, "vendor", val);
                  }}
                  onBlur={e => {
                    const trimmed = e.target.value.trim();
                    if (trimmed && !/\p{L}/u.test(trimmed)) {
                      setItemErrors(prev => {
                        const copy = [...prev];
                        copy[i] = { ...(copy[i] || {}), vendor: t("val_vendor_name_letters", "Vendor name cannot be numeric only") };
                        return copy;
                      });
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/20 ${itemErrors[i]?.vendor ? 'border-red-500' : 'border-border'}`}
                  required
                />
                {itemErrors[i]?.vendor && (
                  <div className="text-red-500 text-xs mt-1">{itemErrors[i].vendor}</div>
                )}
              </div>

              <div>
                <input
                  type="number"
                  placeholder="Amount (₹) *"
                  value={item.amount}
                  onChange={e => updateItem(i, "amount", e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/20 ${itemErrors[i]?.amount ? 'border-red-500' : 'border-border'}`}
                  required
                />
                {itemErrors[i]?.amount && (
                  <div className="text-red-500 text-xs mt-1">{itemErrors[i].amount}</div>
                )}
              </div>

              <div>
                <input
                  placeholder="GST (e.g. 18%) *"
                  value={item.gst}
                  onChange={e => updateItem(i, "gst", e.target.value)}
                  onBlur={e => {
                    const formatted = formatGstValue(e.target.value);
                    if (formatted !== item.gst) {
                      updateItem(i, "gst", formatted);
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/20 ${itemErrors[i]?.gst ? 'border-red-500' : 'border-border'}`}
                  required
                />
                {itemErrors[i]?.gst && (
                  <div className="text-red-500 text-xs mt-1">{itemErrors[i].gst}</div>
                )}
              </div>

              <div>
                <input
                  placeholder="Delivery Time (e.g. 15 Days) *"
                  value={item.timeline}
                  onChange={e => updateItem(i, "timeline", e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/20 ${itemErrors[i]?.timeline ? 'border-red-500' : 'border-border'}`}
                  required
                />
                {itemErrors[i]?.timeline && (
                  <div className="text-red-500 text-xs mt-1">{itemErrors[i].timeline}</div>
                )}
              </div>

              <div>
                <input
                  placeholder="Warranty (e.g. 1 Year) *"
                  value={item.warranty}
                  onChange={e => updateItem(i, "warranty", e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/20 ${itemErrors[i]?.warranty ? 'border-red-500' : 'border-border'}`}
                  required
                />
                {itemErrors[i]?.warranty && (
                  <div className="text-red-500 text-xs mt-1">{itemErrors[i].warranty}</div>
                )}
              </div>

              <div>
                <input
                  type="number"
                  placeholder="Rating (1-5) *"
                  min="1"
                  max="5"
                  step="any"
                  value={item.rating}
                  onKeyDown={e => {
                    if (['-', '+', 'e', 'E'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={e => {
                    const val = e.target.value;
                    updateItem(i, "rating", val);
                    if (val !== '') {
                      const num = parseFloat(val);
                      if (isNaN(num) || num < 1 || num > 5) {
                        setItemErrors(prev => {
                          const copy = [...prev];
                          copy[i] = { ...(copy[i] || {}), rating: 'Rating must be between 1 and 5' };
                          return copy;
                        });
                      }
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/20 ${itemErrors[i]?.rating ? 'border-red-500' : 'border-border'}`}
                  required
                />
                {itemErrors[i]?.rating && (
                  <div className="text-red-500 text-xs mt-1">{itemErrors[i].rating}</div>
                )}
              </div>

              <div className="col-span-1 sm:col-span-2 lg:col-span-3">
                <input
                  placeholder="Remarks / Notes *"
                  value={item.remarks}
                  onChange={e => updateItem(i, "remarks", e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/20 ${itemErrors[i]?.remarks ? 'border-red-500' : 'border-border'}`}
                  required
                />
                {itemErrors[i]?.remarks && (
                  <div className="text-red-500 text-xs mt-1">{itemErrors[i].remarks}</div>
                )}
              </div>
            </div>
          </div>
        ))}

        <Btn xs icon="plus" onClick={addItem}>
          {t("btn_add_vendor")}
        </Btn>
      </div>

      <FileUpload
        label={t("label_supporting_documents")}
        accept="image/*,.pdf"
        multiple
        files={form.files}
        onChange={files => updateField("files", files)}
        note={t("note_upload_scope")}
      />

      <FormActions
        className="pb-10 sm:pb-0"
        onSave={handleSubmit}
        onCancel={onClose}
        label={editQuotation ? t("btn_update_request") : t("btn_create_request")}
        loading={saving}
      />
    </Modal>
  );
}
